import { money } from "./money";

/**
 * What a plan actually charges, said in words.
 *
 * An annual plan is the YEAR, bought: the whole 12-month term is taken in a
 * single payment at the start of it, and again at each renewal. It is not the
 * same plan billed twelve times at a discount, and every string here exists
 * because the checkout used to say it was.
 *
 * The catalog sends `charge_amount` and `charge_interval_months` — the money
 * that moves and how often — alongside `price`, which is only the per-month rate
 * a plan advertises so buyers can compare plans. Where those fields are missing
 * (a catalog response cached from before they existed) the amount is derived the
 * same way the backend derives it.
 */

const TERM_MONTHS = 12;

/**
 * @returns {{amount, everyMonths, isPrepaidTerm, subtotal, tax, taxLabel, taxPercent}}
 * `amount` is the TAX-INCLUSIVE total the card is charged. The catalog sends the
 * breakdown in `charge`; older cached responses have no tax, so it reads as zero
 * rather than being invented client-side.
 */
export function planCharge(plan) {
  if (!plan) {
    return { amount: "", everyMonths: 1, isPrepaidTerm: false,
             subtotal: "", tax: "", taxLabel: "", taxPercent: "" };
  }
  const c = plan.charge || {};
  const everyMonths = Number(plan.charge_interval_months) || (plan.is_annual ? TERM_MONTHS : 1);
  const fallback = plan.is_annual
    ? String(Number(plan.price || 0) * TERM_MONTHS)
    : String(plan.price || "");
  return {
    amount: plan.charge_amount != null ? String(plan.charge_amount) : fallback,
    everyMonths,
    isPrepaidTerm: everyMonths > 1,
    subtotal: c.subtotal != null ? String(c.subtotal) : fallback,
    tax: c.tax != null ? String(c.tax) : "0",
    taxLabel: c.tax_label || "tax",
    taxPercent: c.tax_percent != null ? String(c.tax_percent) : "",
  };
}

/** Has the backend actually quoted a tax amount worth showing? */
export function hasTax(plan) {
  const { tax } = planCharge(plan);
  return Number(tax) > 0;
}

/** The price as a plan card shows it: "$4,788" "/year" or "$399" "/mo". */
export function priceParts(plan) {
  const { amount, isPrepaidTerm } = planCharge(plan);
  return { amount: money(amount), unit: isPrepaidTerm ? "/year" : "/mo" };
}

/** One line under the plan name: what is taken, and when. */
export function termsLine(plan) {
  if (!plan) return "";
  const { amount, isPrepaidTerm } = planCharge(plan);
  if (!isPrepaidTerm) return "Billed monthly. Cancel anytime.";
  const saving = Number(plan.monthly_discount || 0) * TERM_MONTHS;
  const rate = plan.price ? ` That works out to $${money(plan.price)} a month.` : "";
  return (
    `$${money(amount)} for 12 months, charged today. Renews yearly unless you cancel.` +
    (saving > 0 ? ` You save $${money(saving)}.` : rate)
  );
}

/** The same fact on a plan card, where there is room for one line. */
export function cardTermsLine(plan) {
  if (!plan) return "";
  const { amount, isPrepaidTerm } = planCharge(plan);
  if (!isPrepaidTerm) return "Billed monthly, cancel anytime";
  const saving = Number(plan.monthly_discount || 0) * TERM_MONTHS;
  const { subtotal, taxLabel } = planCharge(plan);
  const base = hasTax(plan) ? subtotal : amount;
  return (
    `$${money(base)} billed today for the year` +
    (hasTax(plan) ? ` plus ${taxLabel}` : "") +
    (saving > 0 ? `, saving $${money(saving)}` : "")
  );
}
